import styled from '@emotion/styled';
import { DirectorUser } from '@kaizenplatform/hugot-entity';
import {
  AppBar as _AppBar,
  Avatar,
  Button,
  Chip,
  Divider as _Divider,
  Drawer,
  ListItemIcon,
  ListItemText,
  Select,
  Typography,
} from '@material-ui/core';
import {
  AccountCircle as _AccountCircle,
  AddCircle as _AddCircle,
  ExitToApp as _ExitToApp,
  Help as _Help,
  Home as _Home,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LibraryBooks as _LibraryBooks,
  PlayCircleFilled as _PlayCircleFilled,
  Settings as _Settings,
} from '@material-ui/icons';
import pathToRegexp from 'path-to-regexp';
import { ChangeEvent, ClassAttributes, createElement, Fragment, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';

import Language from '../../../../entity/Language';
import { OrganizationDetailFragment } from '../../../../generated/graphql';
import { useSidebarSP } from '../../../../hooks/useSidebarSP';
import { itemBackground } from '../../../constant/color';
import { SPACING_UNIT } from '../../../constant/spacing';
import { gridLayoutItem } from '../../../styleUtility/gridLayout';
import mq from '../../../styleUtility/mediaquery';
import _KaizenAdLogo from '../../primitive/KaizenAdLogo';

export const HEADER_HEIGHT = 52;
interface Props {
  availableOrganizations: Array<OrganizationDetailFragment>;
  selectedOrganization: OrganizationDetailFragment | null;
  signedInUser: DirectorUser;
  onChangeSelectedOrganization: (organization: OrganizationDetailFragment) => void;
  onClickHomeButton: MouseEventHandler<HTMLElement>;
  onClickProjectsButton: MouseEventHandler<HTMLElement>;
  onClickNewProjectButton: MouseEventHandler<HTMLElement>;
  onClickCreative: MouseEventHandler<HTMLElement>;
  onClickAnalyticsButton: MouseEventHandler<HTMLElement>;
  onClickLogo: MouseEventHandler<HTMLElement>;
  onClickSettings: MouseEventHandler<HTMLElement>;
  onClickAccountSettings: MouseEventHandler<HTMLElement>;
  onClickSignOut: MouseEventHandler<HTMLElement>;
  language: Language;
  className?: string;
  isOpenedByIframe: boolean;
}

const Header = ({
  availableOrganizations,
  selectedOrganization,
  signedInUser,
  onChangeSelectedOrganization,
  onClickHomeButton,
  onClickProjectsButton,
  onClickNewProjectButton,
  onClickCreative,
  onClickSettings,
  onClickAccountSettings,
  onClickSignOut,
  className,
  language,
  isOpenedByIframe,
}: ClassAttributes<HTMLElement> & Props) => {
  const [isOpen, setOpen] = useSidebarSP();
  const handleChangeOrganization = (e: ChangeEvent<{ value: unknown }>) => {
    const orgId = String(e.target.value);
    const org = availableOrganizations.find((org) => org.id === orgId);
    if (!org) {
      return;
    }
    onChangeSelectedOrganization(org);
  };
  return (
    <>
      {isOpenedByIframe ? null : (
        <AppBar color="default" elevation={2} className={className} data-testid="SideBar">
          <LogoContainer onClick={() => setOpen(true)}>
            <KaizenAdLogo />
            <KeyboardArrowDown style={{ marginLeft: '8px' }} />
          </LogoContainer>
        </AppBar>
      )}
      <StyledDrawer anchor={'top'} open={isOpen} onClose={() => setOpen(false)}>
        <Divider />
        {isOpenedByIframe ? null : (
          <CetnerRow onClick={() => setOpen(false)}>
            <Typography>
              <FormattedMessage id="sidebar.close" />
            </Typography>
            <KeyboardArrowUp />
          </CetnerRow>
        )}
        <Divider />
        <UserInfoCol>
          <UserInfoRow>
            {signedInUser.profileImageUrl ? (
              <Avatar
                src={signedInUser.profileImageUrl}
                alt={signedInUser.name}
                style={{ width: '48px', height: '48px' }}
              />
            ) : (
              <AccountCircle color="action" style={{ width: '48px', height: '48px' }} />
            )}
            <div style={{ marginLeft: '12px' }}>
              <Typography>{signedInUser.name}</Typography>
              <Typography>{signedInUser.email}</Typography>
            </div>
          </UserInfoRow>
          {isOpenedByIframe ? null : (
            <Select
              native
              value={selectedOrganization ? selectedOrganization.id : ''}
              onChange={handleChangeOrganization}
              inputProps={{
                name: 'orgs',
                id: 'orgs',
              }}
            >
              {/* nativeモードの場合はMenuItemは使えずoptionを利用する必要がある.  */}
              <option aria-label="None" value="" />
              {availableOrganizations.map((org) => (
                <option value={org.id} key={org.id}>
                  {org.name}
                </option>
              ))}
            </Select>
          )}
        </UserInfoCol>
        <Divider />
        <LeftAlignColumn>
          <SideBarButtonContainerBase
            style={pathToRegexp('/latest-insights').test(location.pathname) ? { background: itemBackground } : {}}
            onClick={(e) => {
              onClickHomeButton(e);
              setOpen(false);
            }}
            // intercom の Product Tour で使用
            data-intercom-target={'sidebar-home-button'}
          >
            <ListItemIcon>
              <Home />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="sidebar.home" />} />
          </SideBarButtonContainerBase>
          <SideBarButtonContainerBase
            style={pathToRegexp('/direction_types').test(location.pathname) ? { background: itemBackground } : {}}
            onClick={(e) => {
              onClickNewProjectButton(e);
              setOpen(false);
            }}
            data-intercom-target={'sidebar-new-project-button'}
          >
            <ListItemIcon>
              <AddCircle />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="sidebar.new_project" />} />
          </SideBarButtonContainerBase>
          <SideBarButtonContainerBase
            style={pathToRegexp('/projects').test(location.pathname) ? { background: itemBackground } : {}}
            onClick={(e) => {
              onClickProjectsButton(e);
              setOpen(false);
            }}
            data-intercom-target={'sidebar-project-list-button'}
          >
            <ListItemIcon>
              <LibraryBooks />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="sidebar.project_list" />} />
          </SideBarButtonContainerBase>
          {(selectedOrganization?.businessRegion.key === 'jp' || (!selectedOrganization && language === 'ja-JP')) && (
            <SideBarButtonContainerBase
              style={pathToRegexp('/search').test(location.pathname) ? { background: itemBackground } : {}}
              onClick={(e) => {
                onClickCreative(e);
                setOpen(false);
              }}
              data-intercom-target={'sidebar-creative-search-button'}
            >
              <ListItemIcon>
                <PlayCircleFilled />
              </ListItemIcon>
              <ListItemText primary={<FormattedMessage id="sidebar.creative" />} />
            </SideBarButtonContainerBase>
          )}
        </LeftAlignColumn>
        <Divider />
        <LeftAlignColumn>
          <SideBarButtonContainerBase
            onClick={(e) => {
              onClickAccountSettings(e);
              setOpen(false);
            }}
          >
            <ListItemIcon>
              <AccountCircle />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="sidebar.account_settings" />} />
          </SideBarButtonContainerBase>
          <SideBarButtonContainerBase
            onClick={(e) => {
              onClickSettings(e);
              setOpen(false);
            }}
          >
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="sidebar.settings" />} />
          </SideBarButtonContainerBase>
          <SideBarButtonContainerBase onClick={onClickSignOut}>
            <ListItemIcon>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="sidebar.sign_out" />} />
          </SideBarButtonContainerBase>
          <HelpButtonContainer href="https://help.kaizen-ad.com/" target="_blank" rel="noopener noreferrer">
            <ListItemIcon>
              <HELP />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="sidebar.help" />} />
          </HelpButtonContainer>
        </LeftAlignColumn>
      </StyledDrawer>
    </>
  );
};

const AppBar = styled(_AppBar)({
  width: '100%',
  paddingLeft: SPACING_UNIT / 2,
  paddingRight: '0 !important',
  right: 'auto',
  left: '0',
  backgroundColor: '#fff',
});

const StyledDrawer = styled(Drawer)({
  padding: '0',
  overflow: 'scroll',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
});

const Divider = styled(_Divider)({
  width: '100%',
  '&:first-of-type': {
    marginTop: 0,
  },
});

const LogoContainer = styled('div')(gridLayoutItem('logo'), {
  textAlign: 'center',
  cursor: 'pointer',
  width: '100%',
  padding: 12,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const KaizenAdLogo = styled(_KaizenAdLogo)({
  paddingBottom: 2,
  width: 100,
});

const DrawerItemRowBase = styled('div')({
  display: 'flex',
});

const CetnerRow = styled(DrawerItemRowBase)({
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  height: HEADER_HEIGHT,
});

const CetnerColumn = styled(DrawerItemRowBase)({
  width: '100%',
  flexDirection: 'column',
});

const UserInfoCol = styled(CetnerColumn)({
  padding: 16,
});

const UserInfoRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: '8px',
});

const SideBarButtonContainerBase = styled('div')({
  display: 'flex',
  cursor: 'pointer',
  width: '100%',
  padding: `${SPACING_UNIT * 1.5}px ${SPACING_UNIT}px`,
});

const HelpButtonContainer = styled('a')({
  display: 'flex',
  cursor: 'pointer',
  width: '100%',
  padding: `${SPACING_UNIT * 1.5}px ${SPACING_UNIT}px`,
  textDecoration: 'none',
  '&:hover': {
    background: itemBackground,
    transition: 'background 0.3s ease',
  },
});

const HeaderIconBase = {
  width: 24,
  height: 24,
  color: '#4C566C',
};

const Home = styled(_Home)(HeaderIconBase, {});
const AddCircle = styled(_AddCircle)(HeaderIconBase, {});
const LibraryBooks = styled(_LibraryBooks)(HeaderIconBase, {});
const PlayCircleFilled = styled(_PlayCircleFilled)(HeaderIconBase, {});
const Settings = styled(_Settings)(HeaderIconBase, {});
const ExitToApp = styled(_ExitToApp)(HeaderIconBase, {});
const AccountCircle = styled(_AccountCircle)(HeaderIconBase, {});
const HELP = styled(_Help)(HeaderIconBase, {});

const LeftAlignColumn = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'left',
  flexDirection: 'column',
  padding: '12px',
});

export const ProjectsButton = styled(Button)(gridLayoutItem('link_buttons'), {
  [mq('sp')]: {
    display: 'none',
  },
});

export const AnalyticsButton = styled(Button)(gridLayoutItem('analytics_buttons'), {
  [mq('sp')]: {
    display: 'none',
  },
});

export const ButtonChip = styled(Chip)({
  height: 20,
  fontSize: 8,
  marginLeft: SPACING_UNIT / 2,
});

const Label = styled(Typography)({
  color: '#4C566C',
  fontWeight: 500,
  fontSize: '0.7em',
  marginTop: '2px',
  marginBottom: SPACING_UNIT,
  whiteSpace: 'nowrap',
});
Label.defaultProps = { variant: 'caption' };

export default Header;
